/**
 * The past seasons summary body: loading, failed, empty and ready states for
 * one season's leaderboard (ISSUE-754, S8, D2). Read only: no revive, no
 * nudge, no button of any kind in the ready state. Every number rendered here
 * is a server field; nothing is summed, sorted or ranked in this file (R2,
 * D3 of the plan).
 */
import { InlineBanner, Skeleton } from "@sportsmet/ui";
import type { JSX } from "react";

import { medalLine, trophyLine } from "../awards/awardCounts";
import {
  PAST_SEASON_EMPTY_MESSAGE,
  PAST_SEASON_FAILED_MESSAGE,
  PAST_SEASON_RETRY_LABEL,
  pointsLabel,
  type typePastSeasonSummaryPhase,
  type typeSeasonLeaderboardMember,
} from "./pastSeasonsModel";

export type typePastSeasonSummaryProps = {
  phase: typePastSeasonSummaryPhase;
  members: readonly typeSeasonLeaderboardMember[];
  onRetry: () => void;
};

export const PastSeasonSummary = ({
  phase,
  members,
  onRetry,
}: typePastSeasonSummaryProps): JSX.Element => {
  if (phase === "loading") {
    return (
      <div data-testid="past-season-summary-loading">
        <Skeleton shape="card" />
      </div>
    );
  }

  if (phase === "failed") {
    return (
      <div data-testid="past-season-summary-failed">
        <InlineBanner
          variant="warning"
          message={PAST_SEASON_FAILED_MESSAGE}
          action={{ label: PAST_SEASON_RETRY_LABEL, onAction: onRetry }}
        />
      </div>
    );
  }

  if (phase === "empty") {
    return (
      <p
        className="text-body text-ink-secondary"
        data-testid="past-season-summary-empty"
      >
        {PAST_SEASON_EMPTY_MESSAGE}
      </p>
    );
  }

  return (
    <ol className="flex flex-col gap-3" data-testid="past-season-leaderboard">
      {members.map((member) => (
        <li key={member.userId} data-testid="past-season-row">
          <div className="flex justify-between gap-2">
            <span
              className="tabular-nums text-body text-ink"
              data-testid="past-season-rank"
            >
              {member.rank}
            </span>
            <span className="text-body text-ink">{member.displayName}</span>
            <span
              className="tabular-nums text-body text-ink"
              data-testid="past-season-points"
            >
              {pointsLabel(member.pointsTotal)}
            </span>
          </div>
          <div className="flex flex-col gap-2 text-caption text-ink-secondary">
            <span>{medalLine(member)}</span>
            <span>{trophyLine(member)}</span>
          </div>
        </li>
      ))}
    </ol>
  );
};
